import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light";

interface ThemeContextType {
	theme: Theme;
	toggleTheme: () => void;
	setTheme: (theme: Theme) => void;
	isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [theme, setThemeState] = useState<Theme>("dark");
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
		const savedTheme = localStorage.getItem("ke-theme") as Theme | null;
		if (savedTheme === "light" || savedTheme === "dark") {
			setThemeState(savedTheme);
		} else {
			// Default to dark as requested
			setThemeState("dark");
		}
	}, []);

	useEffect(() => {
		if (!mounted) return;
		const root = document.documentElement;
		if (theme === "dark") {
			root.classList.add("dark");
			document.body.classList.add("dark");
			document.body.style.backgroundColor = "#15151A";
			document.body.style.color = "#F8FAFC";
		} else {
			root.classList.remove("dark");
			document.body.classList.remove("dark");
			document.body.style.backgroundColor = "#FFFFFF";
			document.body.style.color = "#0F5A41";
		}
		localStorage.setItem("ke-theme", theme);
	}, [theme, mounted]);

	const toggleTheme = () => {
		setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
	};

	const setTheme = (newTheme: Theme) => {
		setThemeState(newTheme);
	};

	return (
		<ThemeContext.Provider value={{ theme, toggleTheme, setTheme, isDark: theme === "dark" }}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = (): ThemeContextType => {
	const context = useContext(ThemeContext);
	if (!context) {
		// Fallback safe default if rendered outside provider
		return {
			theme: "dark",
			toggleTheme: () => {},
			setTheme: () => {},
			isDark: true,
		};
	}
	return context;
};
